import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLocalization } from '@/hooks/useLocalization';
import { SavedInvestorFilter } from '@/store/settingsApi';
import SavedFilterEditor from './SavedFilterEditor';

interface ChangeSavedFilterDialogProps {
  open: boolean;
  filter: SavedInvestorFilter | null;
  onClose: () => void;
  onSave: (update: {
    excludedAccounts: number[];
    excludedCategories: number[];
    excludedAccountTypes: number[];
  }) => void | Promise<void>;
}

const ChangeSavedFilterDialog: React.FC<ChangeSavedFilterDialogProps> = ({
  open,
  filter,
  onClose,
  onSave,
}) => {
  const { t } = useLocalization();

  const [excludedAccounts, setExcludedAccounts] = React.useState<number[]>([]);
  const [excludedCategories, setExcludedCategories] = React.useState<number[]>(
    [],
  );
  const [excludedAccountTypes, setExcludedAccountTypes] = React.useState<
    number[]
  >([]);

  React.useEffect(() => {
    if (filter) {
      setExcludedAccounts(filter.filters.ExcludedAccounts || []);
      setExcludedCategories(filter.filters.ExcludedCategories || []);
      setExcludedAccountTypes(filter.filters.ExcludedAccountTypes || []);
    }
  }, [filter]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h6'>
          {t('changeSavedFilter', 'Change Saved Filter')}
          {filter ? `: ${filter.text}` : ''}
        </Typography>
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {filter ? (
          <SavedFilterEditor
            excludedAccounts={excludedAccounts}
            excludedCategories={excludedCategories}
            excludedAccountTypes={excludedAccountTypes}
            onChange={({
              excludedAccounts,
              excludedCategories,
              excludedAccountTypes,
            }) => {
              setExcludedAccounts(excludedAccounts);
              setExcludedCategories(excludedCategories);
              setExcludedAccountTypes(excludedAccountTypes);
            }}
          />
        ) : null}
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          size='large'
          variant='outlined'
          onClick={onClose}
        >
          {t('cancel', 'Cancel')}
        </Button>
        <Button
          size='large'
          variant='contained'
          disabled={!filter}
          onClick={async () => {
            if (!filter) return;
            await onSave({
              excludedAccounts,
              excludedCategories,
              excludedAccountTypes,
            });
            onClose();
          }}
        >
          {t('save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeSavedFilterDialog;
